// Your routes — and the navigation binding's, which is the part worth knowing.
//
// THIS FILE IS REPLACED when the navigation shell is re-applied. `bottom-tabs`,
// `stack` and `sidebar` produce different routers, so the binding owns it and
// `appfactory spec apply` rewrites it with `overwrite: true`.
//
// So: edit it freely, and commit before running `spec apply`. Everything below
// is yours until the shell itself changes.

import { createRouter, createMemoryHistory } from '@ionic/vue-router';
import { homeOutline, listOutline } from 'ionicons/icons';

import TabsShell from './pages/TabsShell.vue';

// The screens themselves come from ./product-routes.js, which is generated from
// .appfactory/product-plan.json by `appfactory product apply`.
//
// Two owners, one file each. THIS file is the shell; the PRODUCT owns which
// screens exist.
import { PRODUCT_ROUTES, PRIMARY_ROUTES, HOME_ROUTE } from './product-routes.js';

/**
 * A product path, made relative to the tab shell.
 *
 * The plan states routes ABSOLUTELY — `/`, `/tasks/:id` — because that is what
 * a screen's route means to everyone except vue-router. A child route with a
 * leading slash is mounted at the ROOT instead of under its parent, so the
 * shell wraps nothing and its outlet stays empty.
 *
 * The old strip only handled paths that already began `/tabs/`, which no
 * product plan produces, so every child kept its leading slash.
 */
const rel = (p) => String(p).replace(/^\/tabs\//, '').replace(/^\//, '');

/**
 * Where the app opens. main.js pushes this.
 *
 * INSIDE the shell, deliberately. This was `HOME_ROUTE` — the raw plan path —
 * and this app's primary screen is routed at `/`, so the root route became
 * `{ path: '/', redirect: '/' }`. vue-router resolves that redirect, arrives at
 * the same route, resolves it again, and the app died at boot with
 * `RangeError: Maximum call stack size exceeded` and a blank page. Nothing
 * rendered, which is also why every store screenshot came out empty.
 */
export const HOME = `/tabs/${rel(HOME_ROUTE)}`;

/** Icons a generated tab falls back to, in order. Reorder or replace freely. */
const TAB_ICONS = [homeOutline, listOutline];

// The tabs, in bar order, with the icon each one wears.
//
// `icon` is an IMPORTED ionicon, not a name. `<ion-icon name="home">` fetches
// its SVG from a CDN at runtime, which the app's content-security-policy blocks
// and an offline app could not reach anyway — you get an invisible tab bar that
// works fine on your machine and is blank on a plane.
export const TABS = [
  ...PRIMARY_ROUTES.map((r, i) => ({
    name: r.id,
    label: r.label,
    // Shell-prefixed, like every other link into the product: a tab pointing at
    // the bare plan path navigates out of the shell and loses the tab bar.
    path: `/tabs/${rel(r.path)}`,
    // A tab without an icon renders as a label floating in empty space, so
    // there is always one. Change them freely — this array is yours.
    icon: TAB_ICONS[i % TAB_ICONS.length],
  })),
];

export default createRouter({
  history: createMemoryHistory(),
  routes: [
    { path: '/', redirect: HOME },
    {
      // The tab shell owns its own outlet, so each tab keeps its own navigation
      // stack. Pushing /tabs/tasks/3 leaves the tab bar in place and the back
      // button working.
      path: '/tabs/',
      component: TabsShell,
      // The empty child is NOT a redirect. Once paths are relative the home
      // screen occupies that slot itself, and sending it onward to a route that
      // resolves back here is the loop described on HOME above.
      children: [
        // `props: true` on every route, so a screen with `:id` in its path gets
        // it as a prop as well as through useRoute().
        ...PRODUCT_ROUTES.map((r) => ({
          path: rel(r.path),
          name: r.id,
          component: r.component,
          props: true,
        })),
      ],
    },
  ],
});
